import {
  BellRing,
  CalendarDays,
  Clock,
  Coins,
  FileText,
  HandCoins,
  LayoutGrid,
  MonitorPlay,
  ShieldCheck,
  Sunrise,
  Tablet,
  Users,
} from 'lucide-react'
import type { ComponentType, ReactElement } from 'react'
import {
  CampaignsShot,
  LuachShot,
  MembersShot,
  MessageShot,
  OutstandingShot,
  RuleShot,
  ScheduledShot,
  SimchaShot,
  StaffShot,
  YahrzeitShot,
  YearShot,
} from './shots'

/**
 * What the product covers, and how far each part goes.
 *
 * The card is the promise; `detail` is the evidence. They are one object
 * because they must not be able to disagree — a card claiming something the
 * deep dive does not show is the failure mode a marketing page slides into
 * quietest.
 *
 * ## How the copy here is written
 *
 * In the reader's words, not ours. The people running a shul are not
 * technical, and nothing on this page asks them to learn our vocabulary to
 * understand what they are buying: no rules-and-anchors, no schema, no "the
 * same component renders the PDF". What they get told is what changes on a
 * Tuesday afternoon in their office.
 *
 * `depth` is the part that earns the product its claim. Anyone can say
 * "handles Yom Tov"; the list says *which* awkward cases, in the form a gabbai
 * would recognize them — because recognizing your own edge case in somebody
 * else's list is the only real proof that they have met it before.
 */

export type Feature = {
  key: string
  icon: ComponentType<{ className?: string }>
  /** Names the area. Sits above the headline as a small label, so the headline
   *  never has to spend itself saying which section this is. */
  title: string
  /**
   * The band's HEADLINE — the pain this area takes away, in the shul's own
   * words. It is the argument, so it names a specific awkward case rather than
   * summarizing a capability: "Mincha moves on the Fridays that need it", not
   * "flexible scheduling".
   *
   * These have to come from a shul rather than from us. What is written here is
   * a first pass to be replaced, not a finished claim.
   */
  lead: string
  /** One or two sentences under the headline: why that is true. */
  body: string
  detail: {
    /** The outcome, stated once at the top of the panel. */
    promise: string
    /**
     * The product, running.
     *
     * `ReactElement`, not `ReactNode`, so each shot's own `key` is readable when
     * the panel maps them — which is what lets the list be keyed by identity
     * instead of by array index.
     */
    shots: ReactElement[]
    /** The awkward cases it already knows about. */
    depth: string[]
    /** What you actually do to change it. */
    simple: string
  }
}

export const FEATURES: Feature[] = [
  {
    key: 'schedule',
    icon: CalendarDays,
    lead: 'Mincha moves on the Fridays that need it.',
    title: 'The davening schedule',
    body: 'Every time the shul davens, always current — including the weeks that are nobody’s idea of normal.',
    detail: {
      promise:
        'Describe how your shul davens once. From then on the schedule is right every week, including the weeks that break the pattern.',
      shots: [<RuleShot key="rule" />, <LuachShot key="luach" />],
      depth: [
        'A change you did not know was a rule. It had ramifications somewhere you never thought to look — and the rule reaches them anyway.',
        'Yom Kippur mincha set 85 minutes before Neila — a time anchored to another time, not to the sun — so the fast ends when it should however the day falls.',
        'Mincha 40 minutes before shkia on Shabbos but 20 on Yom Tov, because on Shabbos people go home afterwards to eat seudah shlishis.',
        'Maariv on night one of Sukkos as early as halachically possible, in case of rain, so people can still eat in the sukkah — and five minutes later on night two, where there is no melacha to wait for.',
        'The Shabbos before a Tisha b’Av that got pushed to Sunday davens mincha at a fixed 6:00 — with the reason beside it, so nobody “corrects” it in five years.',
        'A minhag your shul has and the shul down the road does not.',
        'Three lines or twenty-five, the sheet is laid out properly and fits its page.',
      ],
      simple:
        'Say what moved and what it should be now. Every sheet, screen and message it appears on changes with it.',
    },
  },
  {
    key: 'zmanim',
    icon: Sunrise,
    lead: 'Every zman says which opinion it follows, and is computed for where your shul is.',
    title: 'Zmanim',
    body: 'Computed for your building, to your shitos — not bought in from a service that does not know where you are.',
    detail: {
      promise:
        'The times are yours: your coordinates, your elevation, and the shita your Rov holds by for each one.',
      shots: [<LuachShot key="luach" />],
      depth: [
        'Every zman states which opinion it follows. “72 minutes” means one thing as clock minutes and another as degrees, and the difference is about twenty-five minutes in June — so it is written down rather than assumed.',
        'A deadline rounds down and a time you wait for rounds up. The rounding always goes the safe way for what that time is.',
        'Sea level or your real elevation, because it changes sunset — and every davening time that follows from it.',
        'One-day or two-day Yom Tov, and the right number of Yizkors, depending on where you are.',
        'Checked against a luach a real congregation davens by, to the printed minute.',
      ],
      simple:
        'Set once when the shul is set up. After that nobody touches it — there is nothing to renew and nothing to paste in each year.',
    },
  },
  {
    key: 'year',
    icon: Clock,
    lead: 'The fast that lands on Shabbos moves itself.',
    title: 'The Jewish year',
    body: 'The awkward years handle themselves — a fast pushed off, a three-day Yom Tov, a second Adar.',
    detail: {
      promise:
        'Every year is a different shape, and none of them is a special case you have to prepare for.',
      shots: [<YearShot key="year" />],
      depth: [
        'A fast that falls on Shabbos and moves to Sunday, with the times that go with it.',
        'Yom Tov running into Shabbos, so eruv tavshilin belongs on the sheet — and stays off it in the years it does not.',
        'A leap year, and Purim landing in the second Adar.',
        'Erev Pesach on Shabbos, which changes when chometz has to be gone.',
        'Chol Hamoed davening that differs from both the chag and an ordinary weekday.',
      ],
      simple:
        'You do nothing. There is no “roll over the year” — you open next Pesach and it is already the right one.',
    },
  },
  {
    key: 'publishing',
    icon: FileText,
    lead: 'The wall, the phone and the message never disagree.',
    title: 'The luach, printed and public',
    body: 'One schedule, wherever people read it: the sheet on the wall, the page you send, the screen in the lobby.',
    detail: {
      promise:
        'The printed sheet, your public page and the weekly message can never disagree, because there is only one schedule underneath them.',
      shots: [<LuachShot key="luach" />],
      depth: [
        'A letter page per occasion, set in Hebrew, fitted to one page — no fighting a layout the night before.',
        'A public page anyone can open without an account, on your own address.',
        'The same times inside a message, filled in when it sends rather than typed when it is written.',
        'Change a time in the morning and every one of those is already right by the afternoon.',
      ],
      simple:
        'Print it, or send the link. There is no “export” step and nothing to re-save when a time changes.',
    },
  },
  {
    key: 'members',
    icon: Users,
    lead: 'A family changes once, not in five places.',
    title: 'Members and families',
    body: 'One record per person, shared by everything else — so a name changes in one place, not five.',
    detail: {
      promise:
        'The person you bill, the person you seat and the person you text are the same record, so they cannot fall out of step.',
      shots: [<MembersShot key="members" />, <YahrzeitShot key="yahr" />],
      depth: [
        'Households and the people in them, including the ones who have moved out and kept their yahrzeits with you.',
        'Hebrew names for the aliya, beside the English name the office uses.',
        'The whole family graph — sons, sons-in-law, married-out daughters, and which guests belong to which family, so a simcha reaches the people it actually concerns.',
        'Yahrzeits on the Hebrew date, which is why they land correctly every year without anyone moving them.',
        'Relationships that survive a change of address, a marriage, or a family splitting into two households.',
      ],
      simple:
        'Update the person. Everything pointing at them is already updated, because it was never a copy.',
    },
  },
  {
    key: 'messaging',
    icon: BellRing,
    lead: 'The weekly times go out on their own. So do the simchos.',
    title: 'Messaging',
    body: 'The two things a shul sends — the week’s times, and the simchos — each handled end to end.',
    detail: {
      /*
       * Both streams, named. The panel demonstrates a simcha invitation
       * directly beneath this line, so a promise about the week's times alone
       * would argue one thing and show another.
       */
      promise:
        'A shul sends two kinds of message: the week’s times, and the simchos. The times fill themselves in; a simcha is read off the invitation. Both reach exactly the people they should.',
      shots: [
        <SimchaShot key="simcha" />,
        <MessageShot key="msg" />,
        <ScheduledShot key="sched" />,
      ],
      depth: [
        'A send set against the occasion rather than a date, so it recurs for ever and is never rebuilt.',
        'Held back on the weeks it should not go — Shabbos, Yom Tov, and the weeks a different sheet applies. That is the difference between automated and nearly automated: if somebody still checks which Fridays are wrong, nothing came off their plate.',
        'An invitation read from a photograph, Hebrew included — which is where most text recognition quietly gives up.',
        'Who is hosting, which grandparents to name, whether it is a couple or one person, and the words a shul actually uses for each kind of simcha.',
        'A boy born means two events: a shalom zachor on Friday night and a bris eight days on. Ask for a reminder the morning of the bris and it works out which morning that is.',
        'Email and text from one message, each in the shape that channel needs.',
        'A test send that reaches only you, so nothing goes to the whole shul by accident.',
        'People choose what they hear about and how, and an unsubscribe is binding.',
      ],
      simple:
        'Somebody sends you an invitation and needs it out now, while you are in the middle of five other things. Upload it and say when.',
    },
  },
  {
    key: 'billing',
    icon: Coins,
    lead: 'Who still owes, without adding anything up.',
    title: 'Dues and billing',
    body: 'What each family owes and what they have paid, with a real ledger behind it.',
    detail: {
      promise:
        'You can answer “what do I owe?” in one look, and the answer is arrived at rather than typed in.',
      shots: [<OutstandingShot key="out" />],
      depth: [
        'Membership, seats and appeals as separate charges against the same family.',
        'Part payments, so a family paying in installments is not either “paid” or “unpaid”.',
        'A statement a family can be sent, showing how the number was arrived at.',
        'A run you can repeat safely — billing twice by accident does not charge twice.',
        'Card details live with the payment processor and never with us. Not stored, not shown, not in a spare column.',
      ],
      simple:
        'Ask who has not paid and you get the list — then send it to exactly those families without building an audience by hand.',
    },
  },
  {
    key: 'giving',
    icon: HandCoins,
    lead: 'Every gift lands against the right appeal.',
    title: 'Donations and appeals',
    body: 'Giving that lands in the same ledger, credited to the appeal it was given for.',
    detail: {
      promise:
        'Money given from the phone, the lobby or the office all arrives in the same place, already attributed.',
      shots: [<CampaignsShot key="camps" />],
      depth: [
        'Maos chittim, matonos l’evyonim, a building drive — each one an appeal that gifts are credited against.',
        'A public page on your own processor account, so the money is yours from the moment it is given.',
        'A gift from a member is attached to their record; a gift from a stranger is not forced to invent one.',
        'What an appeal has raised, against what it was hoping for, counted as the gifts arrive.',
        'Card details never reach us — the processor holds them, and we hold a reference.',
      ],
      simple:
        'Open an appeal, share the link. Everything given against it is counted from that moment.',
    },
  },
  {
    key: 'office',
    icon: ShieldCheck,
    lead: 'The treasurer does the money without doing everything.',
    title: 'Who can do what',
    body: 'The gabbai runs the schedule, the treasurer runs the money, and neither has to go through you.',
    detail: {
      promise:
        'Several people can run the shul without sharing one login or seeing everything.',
      shots: [<StaffShot key="staff" />],
      depth: [
        'A role says who somebody is — gabbai, treasurer, Rov — and what they can reach follows from it.',
        'Someone can be given one thing without being given everything.',
        'Member details are behind a login. The public page shows times, never people.',
        'What your shul can see is your shul’s. Other congregations on the platform cannot see any of it.',
      ],
      simple:
        'Add the person, give them their role. Taking it away later is the same one step.',
    },
  },
]

/** Named honestly, and kept out of the feature set — a roadmap item printed as
 *  a feature is a promise the page cannot keep. */
export const PLANNED = [
  {
    icon: MonitorPlay,
    title: 'A screen in the lobby',
    body: 'Today’s times and this week’s announcements, off the same schedule.',
  },
  {
    icon: LayoutGrid,
    title: 'Seating',
    body: 'Places for the Yamim Noraim, against the families you already have.',
  },
  {
    icon: Tablet,
    title: 'Giving in the building',
    body: 'A terminal in the lobby, landing in the same ledger as everything else.',
  },
]
